/** "What you missed" sequencing on the client. The beats themselves come from the server. Pure. */
import type { GameState, Rewind, RewindBeat } from '@clutch/core';

export const BEAT_MS = 2000;
export const CAUGHT_UP_MS = 1600;
export const CAUGHT_UP = 'You’re caught up — call it.';

export type RewindStep = { kind: RewindBeat['kind'] | 'caught_up'; text: string; seq?: number; beat?: RewindBeat };

/** Should a fan opening this room see a rewind? Only after tip-off, before the final, once. */
export function shouldRewind(state: Pick<GameState, 'status' | 'asOfSeq'>, seen: boolean): boolean {
  return !seen && state.status === 'live' && state.asOfSeq > 0;
}

/**
 * The steps to play: 3–5 real beats (server order, `now` last), then "You're caught up".
 * Fewer than one story beat besides `now` → nothing worth a rewind (returns []).
 * Beats with blank text are dropped; nothing is added that the server didn't send.
 */
export function rewindSteps(r: Rewind | undefined): RewindStep[] {
  if (!r) return [];
  const beats = r.beats.filter((b) => typeof b.text === 'string' && b.text.trim().length > 0);
  const now = beats.filter((b) => b.kind === 'now').slice(-1);
  const story = beats.filter((b) => b.kind !== 'now');
  if (story.length === 0) return [];
  const kept = [...story.slice(0, 5 - now.length), ...now];
  return [
    ...kept.map((b): RewindStep => ({ kind: b.kind, text: b.text, ...(b.seq !== undefined ? { seq: b.seq } : {}), beat: b })),
    { kind: 'caught_up', text: CAUGHT_UP },
  ];
}

/** Total auto-play time, for tests and the progress bar. */
export function rewindDurationMs(steps: readonly RewindStep[]): number {
  return steps.reduce((t, s) => t + (s.kind === 'caught_up' ? CAUGHT_UP_MS : BEAT_MS), 0);
}

export const rewindSeenKey = (roomId: string, fanId: string) => `clutch.rewind.${roomId}.${fanId}`;
